import Vue from 'vue';
import User from './user';

// https://developer.mozilla.org/en-US/docs/Web/HTTP/Authentication#Authentication_schemes
export type AuthenticationScheme = 'Basic' | 'Digest' | 'Bearer' | 'HOBA' | 'Mutual' | 'AWS4-HMAC-SHA256';
export type AuthRole = 'none' | 'admin' | 'hostadmin' | 'user' | 'guest' | string;

export default abstract class AuthenticationStrategy {
  abstract isLoggedIn: boolean;
  /**
   * Is authentication stale? If it needs a refresh call refresh method.
   */
  abstract needsRefresh: boolean;
  abstract roles?: AuthRole[];
  abstract claims: Record<string, string>;
  abstract user?: User;

  abstract getUser(): Promise<User | undefined>;
  /**
   * Refresh authentication data
   */
  abstract refresh(): Promise<any>;
  /**
   * Perform login
   */
  abstract login(data: any): Promise<any>;
  /**
   * Perform logout and clear authentication data
   */
  abstract logout(): Promise<any>;

  clearAuthenticationHeader() {
    delete Vue.$fetch.defaults.headers.Authorization;
  }

  setAuthenticationHeader(scheme?: AuthenticationScheme, credentials?: any) {
    if (scheme && credentials) {
      Vue.$fetch.defaults.headers.Authorization = `${scheme} ${credentials}`;
    } else if (credentials) {
      Vue.$fetch.defaults.headers.Authorization = `${credentials}`;
    }
  }

  /** Get claim by normalized name */
  getClaim(claimName: string): string | undefined {
    if (!this.claims) {
      return undefined;
    }
    const claimMap = {} as Record<string, any>;
    Object.keys(this.claims).forEach((key) => {
      // First letter to lower, unless directly followed by an uppercase character
      const mappedName = key.replace(/^\w[^A-Z]/, (c) => c.toLowerCase());
      claimMap[mappedName] = this.claims[key];
    });
    return claimMap[claimName];
  }

  isInRole(...roles: string[]) {
    if (!this.roles) {
      return false;
    }
    return roles.some((role) => {
      return this.roles?.some((userRole) => role.toLowerCase() === userRole?.toLowerCase() || role === userRole);
    });
  }

  /**
   * Redirect to login page as defined in configuration file,
   * Will not work if there is no redirect set in config
   */
  async redirectToLogin() {
    if (Vue.$router.history.ready) {
      const loginPath = Vue.$config.values['auth-redirects-login'];
      if (loginPath) {
        Vue.$log.debug('🛂 [Auth] Redirecting to login.');
        await Vue.$routerUtils.redirect(loginPath);
      } else {
        Vue.$log.error('🛂 [Auth] Unable to redirect to login, no redirect was configured.');
      }
    } else {
      Vue.$log.error('🛂 [Auth] Unable to redirect to login, router is not ready.');
    }
  }
}
